import React from 'react';
import { formatTanggalIndo } from '@/lib/timeUtils';
import { terbilang } from '@/lib/pdfGenerator';

const fmt = (n) => n ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '0';

export default function BillingStatementPrint({ data }) {
  if (!data) return null;

  const { clientName, invoices, totalTagihan, bank, companySettings: rawCompanySettings } = data;
  
  const companySettings = {
    ...rawCompanySettings,
    stempel_url: rawCompanySettings?.stempel_url || '/stempel-invoice.png',
    ttd_url: rawCompanySettings?.ttd_url || '/ttd-invoice.png',
    logo_url: rawCompanySettings?.logo_url || '/logo-invoice.png'
  };

  // Urutkan invoice dari yang terlama ke terbaru
  const sortedInvoices = [...invoices].sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="hidden print:block font-sans text-black p-0 text-[12px] leading-relaxed w-full">
      <style type="text/css">{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: white !important; }
        }
      `}</style>
      
      <div id="invoice-print-area" className="bg-white p-0 max-w-none w-full mx-auto border-none shadow-none">
        
        {/* Company Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            {companySettings.logo_url ? (
              <img src="/logo.png" alt="Logo" className="w-16 h-16 object-contain" />
            ) : (
              <img src="/logo.png" alt="Logo" className="w-16 h-16 object-contain" />
            )}
            <div>
              <h1 className="text-xl font-black text-slate-900 tracking-tight uppercase" style={{ fontFamily: "'Arial Black', sans-serif" }}>
                {companySettings.nama_perusahaan || 'PT. SATALA DERMATECH ESSENTIAL'}
              </h1>
              <p className="text-[10px] text-slate-600 font-medium max-w-[320px] mt-1 leading-snug">
                {companySettings.alamat || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat'}
              </p>
            </div>
          </div>
          <div className="text-right flex flex-col items-end">
            <div className="bg-[#8C6208] print:bg-[#8C6208] text-white px-5 py-1.5 rounded-lg mb-3 shadow-sm border border-[#8C6208]">
              <h2 className="text-lg font-black tracking-widest uppercase whitespace-nowrap" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>REKAP TAGIHAN</h2>
            </div>
          </div>
        </div>

        <div className="w-full h-[2px] bg-slate-200 mb-6"></div>

        {/* Info Grid */}
        <div className="mb-6 flex justify-between items-start">
          <div className="space-y-1 p-4 border border-[#8C6208]/30 rounded-lg bg-slate-50 min-w-[280px]">
            <p className="text-[9px] text-[#8C6208] font-black uppercase tracking-widest mb-2">Ditagihkan Kepada:</p>
            <p className="font-black text-[#8C6208] text-sm uppercase">{clientName}</p>
            <p className="text-[10px] text-slate-600 font-medium leading-relaxed mt-1">
              Pembayaran Tagihan Produk OEM / Maklon<br />
              Di Tempat.
            </p>
          </div>
          <div className="p-4 border border-[#8C6208]/30 rounded-lg min-w-[220px]">
            <div className="flex justify-between text-[10px] mb-2">
              <span className="text-slate-500 font-bold uppercase">Tanggal Cetak</span>
              <span className="font-bold text-slate-900">{formatTanggalIndo(new Date().toISOString().split('T')[0])}</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span className="text-slate-500 font-bold uppercase">Status Tagihan</span>
              <span className="font-black text-rose-600 tracking-wider">UNPAID</span>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className="font-bold text-slate-800 mb-1">Dengan hormat,</p>
          <p className="text-slate-600 text-xs">Bersama surat ini, kami sampaikan rincian tagihan yang saat ini tercatat belum dilunasi sepenuhnya:</p>
        </div>

        {/* Invoice Table */}
        <table className="w-full text-left border-collapse mb-4 border border-[#8C6208]/20">
          <thead>
            <tr className="bg-[#8C6208] text-white border-b-2 border-[#8C6208]" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <th className="py-3 px-3 font-bold text-[10px] uppercase tracking-wider w-12 border-r border-white/20 text-center">No</th>
              <th className="py-3 px-3 font-bold text-[10px] uppercase tracking-wider border-r border-white/20">Nomor Invoice / PO</th>
              <th className="py-3 px-3 font-bold text-[10px] uppercase tracking-wider w-32 border-r border-white/20 text-center">Tanggal Inv.</th>
              <th className="py-3 px-3 font-bold text-[10px] uppercase tracking-wider text-right w-40">Sisa Tagihan</th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {sortedInvoices.map((inv, idx) => (
              <React.Fragment key={idx}>
                <tr className={inv.products && inv.products.length > 0 ? "border-none" : "border-b border-[#8C6208]/10"}>
                  <td className={`py-2.5 px-3 text-slate-600 border-r border-[#8C6208]/10 text-center ${inv.products && inv.products.length > 0 ? 'align-top pt-3' : ''}`}>{idx + 1}</td>
                  <td className={`py-2.5 px-3 font-bold text-[#8C6208] border-r border-[#8C6208]/10 ${inv.products && inv.products.length > 0 ? 'align-top pt-3' : ''}`}>
                    {inv.id}
                    {inv.products && inv.products.length > 0 && (
                      <ul className="mt-1.5 list-none text-[9px] text-slate-500 font-medium space-y-0.5">
                        {inv.products.map((p, pIdx) => {
                          const baseName = p.nama_product || p.botol_nama || p.namaBahan || p.nama_item || p.nama || p.variant || '-';
                          const size = p.botol_ukuran_client || p.botol_ukuran_label || p.botol_ukuran_aktual || p.botol_ukuran_label_ml || p.ukuran || '';
                          const itemName = size ? `${baseName} (${size}ml)` : baseName;
                          const qty = p.qty || p.qty_variant || p.qty_botol || 1;
                          return (
                            <li key={pIdx} className="flex items-start gap-1">
                              <span className="text-slate-300">•</span>
                              <span>{qty} pcs - {itemName}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </td>
                  <td className={`py-2.5 px-3 text-slate-600 border-r border-[#8C6208]/10 text-center ${inv.products && inv.products.length > 0 ? 'align-top pt-3' : ''}`}>{formatTanggalIndo(inv.date)}</td>
                  <td className={`py-2.5 px-3 text-right font-semibold text-slate-800 ${inv.products && inv.products.length > 0 ? 'align-top pt-3' : ''}`}>Rp {fmt(inv.amount)}</td>
                </tr>
              </React.Fragment>
            ))}
            
            <tr className="bg-[#F9F5EC]" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <td colSpan="3" className="py-3.5 px-3 font-black text-[#8C6208] uppercase text-[11px] text-right border-r border-[#8C6208]/20">TOTAL KESELURUHAN</td>
              <td className="py-3.5 px-3 font-black text-[#8C6208] text-right text-base border-t-2 border-[#8C6208]">Rp {fmt(totalTagihan)}</td>
            </tr>
          </tbody>
        </table>

        <div className="mb-6 bg-slate-50/50 p-2.5 rounded-lg border border-slate-100 text-xs">
          <p className="font-bold text-slate-700">Terbilang:</p>
          <p className="text-[10px] italic text-[#8C6208] font-medium mt-0.5">
            {totalTagihan <= 0 ? 'Lunas' : terbilang(totalTagihan)}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 items-start mt-6 text-xs break-inside-avoid">
          <div>
            <p className="font-bold text-slate-700 mb-1">Noted:</p>
            <p className="text-slate-500 text-[10px] mb-3">Pembayaran Harap Mencantumkan Detail Invoice</p>

            {bank && (
              <div className="mt-2 border border-slate-300 p-3 rounded-lg text-[10px] max-w-[320px]">
                <p className="font-bold text-slate-800 mb-2 uppercase tracking-wider text-[9px] border-b border-slate-200 pb-1">Instruksi Pembayaran</p>
                {(() => {
                  let bankName = bank;
                  let bankAccountName = '-';
                  let bankAccountNumber = '-';
                  if (bank.includes(' - ') && bank.includes('(')) {
                    bankName = bank.split(' - ')[0];
                    const rest = bank.split(' - ')[1];
                    bankAccountName = rest.split(' (')[0];
                    bankAccountNumber = rest.match(/\(([^)]+)\)/)?.[1] || '-';
                  } else {
                    bankName = bank;
                  }
                  
                  return (
                    <table className="w-full text-slate-700">
                      <tbody>
                        <tr>
                          <td className="py-0.5 w-24 text-slate-500">Bank</td>
                          <td className="py-0.5 font-bold">: {bankName}</td>
                        </tr>
                        <tr>
                          <td className="py-0.5 w-24 text-slate-500">No Rekening</td>
                          <td className="py-0.5 font-black text-[12px]">: {bankAccountNumber}</td>
                        </tr>
                        <tr>
                          <td className="py-0.5 w-24 text-slate-500">Atas Nama</td>
                          <td className="py-0.5 font-bold">: {bankAccountName}</td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="text-right flex flex-col items-end pr-4 mt-8">
            <div className="relative w-48 h-20 my-1 flex justify-center items-center">
              {companySettings.stempel_url ? (
                <img src={companySettings.stempel_url} alt="Stempel" className="absolute left-6 -top-1 pointer-events-none w-20 h-20 object-contain opacity-80" />
              ) : (
                <div className="absolute left-6 -top-1 pointer-events-none scale-[0.8]">
                  <svg width="90" height="90" viewBox="0 0 100 100" className="opacity-80">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#D4AF37" strokeWidth="2" strokeDasharray="3 2" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#D4AF37" strokeWidth="1" />
                    <text x="50" y="45" fill="#D4AF37" fontSize="8" fontWeight="bold" textAnchor="middle">PT. SATALA</text>
                    <text x="50" y="55" fill="#D4AF37" fontSize="8" fontWeight="bold" textAnchor="middle">DERMATECH</text>
                    <text x="50" y="65" fill="#D4AF37" fontSize="6" fontWeight="bold" textAnchor="middle">★ BOGOR ★</text>
                    <path d="M 45 35 C 45 30, 55 30, 55 35 C 55 40, 45 42, 45 48 C 45 53, 55 53, 55 48" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </div>
              )}

              {companySettings.ttd_url ? (
                <img src={companySettings.ttd_url} alt="Tanda Tangan" className="w-24 h-16 object-contain relative z-10" />
              ) : (
                <span className="font-serif italic text-2xl text-blue-900/60 font-semibold tracking-wider relative z-10 rotate-[-4deg]" style={{ fontFamily: "'Brush Script MT', cursive" }}>
                  {companySettings.nama_direktur || 'Umar Syarif'}
                </span>
              )}
            </div>

            <div className="text-center w-48 border-t border-slate-200 pt-1">
              <p className="font-bold text-slate-800 text-[10px]">{companySettings.nama_direktur || 'Umar Syarif'}</p>
              <p className="text-slate-400 text-[8px] uppercase tracking-wider mt-0.5">Direktur</p>
            </div>
          </div>
        </div>

        <div className="border-b-[2px] border-[#D4AF37] mt-8"></div>
      </div>
    </div>
  );
}

